import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPendingPoEmails, type PoPendingEmail } from '../lib/demPoEmailMonitor';
import { playNotificationSound } from '../lib/notificationSound';

const DEFAULT_POLL_MS = 30_000;

type UsePoEmailMonitorOptions = {
  enabled?: boolean;
  pollIntervalMs?: number;
};

export function usePoEmailMonitor(options: UsePoEmailMonitorOptions = {}) {
  const { enabled = true, pollIntervalMs = DEFAULT_POLL_MS } = options;

  const [emails, setEmails] = useState<PoPendingEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState('—');
  const [newArrivalCount, setNewArrivalCount] = useState(0);

  const prevCountRef = useRef(-1);
  const prevIdsRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef(false);

  const reload = useCallback(async (silent = false) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);

    try {
      const rows = await fetchPendingPoEmails();
      setEmails(rows);
      setError(null);
      setLastSync(
        new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      );

      const currentIds = new Set(rows.map((row) => row.id));
      const brandNew = rows.filter((row) => !prevIdsRef.current.has(row.id));
      const prevCount = prevCountRef.current;

      if (prevCount >= 0 && (brandNew.length > 0 || rows.length > prevCount)) {
        const delta = brandNew.length > 0 ? brandNew.length : rows.length - prevCount;
        if (delta > 0) {
          setNewArrivalCount((c) => c + delta);
          playNotificationSound();
        }
      }

      prevCountRef.current = rows.length;
      prevIdsRef.current = currentIds;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load email queue.';
      if (!silent) setError(message);
    } finally {
      inFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    void reload();
    const timer = window.setInterval(() => {
      void reload(true);
    }, pollIntervalMs);

    return () => window.clearInterval(timer);
  }, [enabled, pollIntervalMs, reload]);

  const clearNewArrivals = useCallback(() => {
    setNewArrivalCount(0);
  }, []);

  return {
    emails,
    count: emails.length,
    loading,
    error,
    lastSync,
    newArrivalCount,
    clearNewArrivals,
    reload,
  };
}
